import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Container,
  Row,
  Col,
  Form,
  Card,
  Alert,
  Spinner,
  InputGroup,
  Accordion,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  FaSearch,
  FaTrashAlt,
  FaCar,
  FaUser,
  FaPhone,
  FaWeightHanging,
  FaRuler,
  FaChevronDown,
  FaChevronUp,
  FaLongArrowAltRight,
  FaIdCard,
  FaTruck,
  FaEdit,
  FaCalendarAlt,
  FaClock,
  FaEye,
  FaRoute,
  FaTimes,
  FaBox,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import "../clientTable/ClientTable.css";
import "./PageOne/DarkTheme.css";
import "../../assets/css/Auftraege.css";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { driverAPI } from "../../api/apiSystem";
import { formatNumber } from "../../utils/helpers";
import PaginationComponent from "../PaginationComponent";
import { useSearchFilter } from "../useSearchFilter";
import ClientChatBox from "../chat/ClientChatBox";

// Import vehicle images
import caddyImg from "../../assets/images/driver_transport_icon/pakete.png";
import busImg from "../../assets/images/driver_transport_icon/palette.png";
import lkwImg from "../../assets/images/driver_transport_icon/moving.png";
import truckImg from "../../assets/images/driver_transport_icon/furniture.png";

// Constants for better maintainability
const VEHICLE_TYPES = [
  "Caddy",
  "Bus",
  "Maxi Bus",
  "Planenbus",
  "LKW (7,5t)",
  "LKW (12t)",
  "LKW (18t)",
  "LKW (40t)",
  "Zugmaschine",
];
const PER_PAGE_OPTIONS = [10, 25, 50];
const INITIAL_FORM_DATA = {
  driver: "",
  vehicle_number: "",
  type: "Caddy",
  phone: "",
  email: "",
  password: "",
  password_confirmation: "",
  weight: "",
  length: "",
  width: "",
  height: "",
};

export default function VehicleTable() {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [validationErrors, setValidationErrors] = useState({});

  // Memoized filtered data
  const filteredData = useSearchFilter(vehicles, searchTerm, [
    "driver_name",
    "vehicle_type",
  ]);

  // Fetch vehicles list with useCallback to prevent unnecessary re-renders
  const fetchVehicles = useCallback(
    async (page = 1, perPage = pagination.per_page) => {
      setLoading(true);
      setError("");

      try {
        const params = { page, per_page: perPage };
        console.log("📦 Fetching vehicles with params:", params);

        const response = await driverAPI.vehicles.index(params);
        console.log("✅ Vehicles API Response:", response);

        if (response.success) {
          // Handle different response structures
          let vehiclesData = [];
          let metaData = {};

          if (Array.isArray(response.data)) {
            vehiclesData = response.data;
            metaData = {
              current_page: page,
              last_page: Math.ceil(response.data.length / perPage),
              per_page: perPage,
              total: response.data.length,
            };
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            vehiclesData = response.data.data;
            metaData = response.data.meta || {
              current_page: page,
              last_page: 1,
              per_page: perPage,
              total: response.data.data.length,
            };
          } else if (
            response.data?.vehicles &&
            Array.isArray(response.data.vehicles)
          ) {
            vehiclesData = response.data.vehicles;
            metaData = response.data.meta || {
              current_page: page,
              last_page: 1,
              per_page: perPage,
              total: response.data.vehicles.length,
            };
          } else {
            console.warn(
              "⚠️ Unexpected API response structure:",
              response.data,
            );
            vehiclesData = [];
            metaData = {
              current_page: page,
              last_page: 1,
              per_page: perPage,
              total: 0,
            };
          }

          setVehicles(vehiclesData);
          setPagination(metaData);
        } else {
          throw new Error(response.message || t("errors.fetchVehicles"));
        }
      } catch (err) {
        console.error("❌ Error fetching vehicles:", err);
        setError(err.message || t("errors.fetchError"));
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    },
    [pagination.per_page, t],
  );

  // Fetch data on component load
  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Handle page change
  const handlePageChange = (page) => {
    fetchVehicles(page);
  };

  // Handle per page change
  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setPagination((prev) => ({ ...prev, per_page: newPerPage }));
    fetchVehicles(1, newPerPage);
  };

  // Update form data
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setValidationErrors({});
  };

  // Add new vehicle
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.password_confirmation) {
      setError(t("errors.passwordMismatch") || "Passwords do not match!");
      return;
    }

    setFormLoading(true);
    setError("");
    setSuccess("");
    setValidationErrors({});

    try {
      const vehicleData = {
        driver_name: formData.driver.trim(),
        vehicle_number: formData.vehicle_number.trim(),
        vehicle_type: formData.type,
        phone_number: formData.phone.trim(),
        email: formData.email.trim(),
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        max_weight: formData.weight || null,
        max_length: formData.length || null,
        max_width: formData.width || null,
        max_height: formData.height || null,
      };

      console.log("🚗 Sending vehicle data:", vehicleData);
      const response = await driverAPI.vehicles.store(vehicleData);
      console.log("✅ Store response:", response);

      if (response.success) {
        await Swal.fire({
          icon: "success",
          title: t("alerts.success"),
          text: t("alerts.vehicleAdded"),
          confirmButtonColor: "#ff7b00",
          timer: 2000,
        });

        resetForm();
        await fetchVehicles(pagination.current_page);
      } else {
        throw new Error(response.message || t("errors.addVehicle"));
      }
    } catch (err) {
      console.error("❌ Error adding vehicle:", err);
      await Swal.fire({
        icon: "error",
        title: t("alerts.error"),
        text: err.message,
        confirmButtonColor: "#ff7b00",
      });
      
      if (err.errors) {
        setValidationErrors(err.errors);
        setError(t("errors.validationFailed") || "Please check the form for validation errors.");
      } else {
        setError(err.message || t("errors.addVehicle") || "Failed to add vehicle.");
      }
    } finally {
      setFormLoading(false);
    }
  };

  // Delete vehicle
  const handleDelete = async (id) => {
    if (!id) {
      await Swal.fire({
        icon: "error",
        title: t("alerts.error"),
        text: t("errors.invalidVehicleId"),
        confirmButtonColor: "#ff7b00",
      });
      return;
    }

    const result = await Swal.fire({
      title: t("alerts.confirmDelete"),
      text: t("alerts.deleteWarning"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("actions.delete"),
      cancelButtonText: t("actions.cancel"),
    });

    if (result.isConfirmed) {
      try {
        const response = await driverAPI.vehicles.delete(id);

        if (response.success) {
          await Swal.fire({
            icon: "success",
            title: t("alerts.deleted"),
            text: t("alerts.vehicleDeleted"),
            confirmButtonColor: "#ff7b00",
            timer: 2000,
          });

          await fetchVehicles(pagination.current_page);
        } else {
          throw new Error(response.message || t("errors.deleteVehicle"));
        }
      } catch (err) {
        console.error("❌ Error deleting vehicle:", err);
        await Swal.fire({
          icon: "error",
          title: t("alerts.error"),
          text: err.message,
          confirmButtonColor: "#ff7b00",
        });
      }
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  // Render highlighted text for search results
  const renderHighlightedText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.toString().split(regex);

    return (
      <span>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <mark key={index} className="bg-warning">
              {part}
            </mark>
          ) : (
            part
          ),
        )}
      </span>
    );
  };

  // Get display data based on search
  const displayData = searchTerm ? filteredData : vehicles;

  // State for accordion
  const [activeKey, setActiveKey] = useState(null);

  // State for mobile offcanvas
  const [offCanvasOpen, setOffCanvasOpen] = useState(null);

  const toggleOffCanvas = (index) => {
    setOffCanvasOpen(offCanvasOpen === index ? null : index);
  };

  // Get vehicle image based on type
  const getVehicleImage = (type) => {
    const typeLower = (type || "").toLowerCase();
    if (typeLower.includes("caddy")) return caddyImg;
    if (typeLower.includes("bus")) return busImg;
    if (typeLower.includes("lkw") || typeLower.includes("truck")) return lkwImg;
    return truckImg;
  };

  return (
    <>
      <NavbarMenu />
      <Container
        fluid
        className="p-4 vehicle-table-container"
        style={{ minHeight: "100vh" }}
      >
        <div className="p-4">
          {/* Title */}
          <h4 className="page-title mb-4">
            {t("vehiclesTable.title")}
          </h4>

          {/* Search + Show Entries */}
          <Row className="mb-3 align-items-center">
            <Col
              xs={12}
              md={6}
              className="d-flex align-items-center mb-2 mb-md-0"
            >
              <span className="me-2 fw-bold dark-text-white">
                {t("clientTable.show")}
              </span>
              <Form.Select
                size="sm"
                style={{ width: "80px" }}
                value={pagination.per_page}
                onChange={handlePerPageChange}
                disabled={loading}
                className="dark-bg-dark dark-text-white border-secondary"
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
              <span className="ms-2 dark-text-white">
                {t("clientTable.entries")}
              </span>
            </Col>

            {/* Search */}
            <Col xs={12} md={6} className="d-flex justify-content-md-end">
              <InputGroup style={{ maxWidth: "300px" }}>
                <InputGroup.Text className="dark-bg-dark border-secondary">
                  <FaSearch className="dark-text-orange" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t("vehicles.search.placeholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="dark-bg-dark dark-text-white border-secondary"
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={clearSearch}
                    title={t("clientTable.clearSearch")}
                    disabled={loading}
                  >
                    ✕
                  </Button>
                )}
              </InputGroup>
            </Col>
          </Row>

          {/* Results Count */}
          {!loading && (
            <Row className="mb-3">
              {/* <Col>
                <div className="dark-text-muted">
                  {t("vehiclesTable.showing")} {displayData.length}{" "}
                  {searchTerm
                    ? t("vehiclesTable.ofFiltered")
                    : t("vehiclesTable.ofTotal")}{" "}
                  {pagination.total} {t("vehiclesTable.vehicles")}
                  {searchTerm && ` ${t("vehiclesTable.for")} "${searchTerm}"`}
                </div>
              </Col> */}
            </Row>
          )}

          {/* Alert messages */}
          {/* {error && (
            <Alert variant="danger" dismissible onClose={() => setError("")}>
              {error}
            </Alert>
          )} */}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}

          {/* Desktop View - Accordion Style */}
          <Accordion
            className="d-none d-md-block dark-accordion-container mb-5"
            activeKey={activeKey}
            onSelect={(key) => setActiveKey(key)}
          >
            {loading && (
              <div className="text-center py-4">
                <Spinner animation="border" variant="warning" />
                <p className="mt-2 dark-text-muted">{t("common.loading")}</p>
              </div>
            )}
            {!loading && displayData.length === 0 && (
              <p className="text-center p-3 dark-text-muted">
                {searchTerm
                  ? t("vehiclesTable.noMatchingVehicles")
                  : t("vehiclesTable.noVehiclesFound")}
                {searchTerm && (
                  <div className="mt-2">
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={clearSearch}
                    >
                      {t("vehiclesTable.clearSearch")}
                    </Button>
                  </div>
                )}
              </p>
            )}
            {!loading &&
              displayData.map((vehicle, index) => (
                <Accordion.Item
                  eventKey={index.toString()}
                  key={vehicle.id || index}
                >
                  <Accordion.Header>
                    <div className="dark-artikel-header w-100">
                      {/* Row 1: Title Row */}
                      <div className="dark-artikel-title-row">
                        <FaTruck
                          style={{ color: "#888", fontSize: "1.5rem" }}
                        />
                        <span className="dark-artikel-title-text">
                          Fahrzeug · #{vehicle.id || index + 1}
                        </span>
                      </div>

                      {/* Row 2: Data Row */}
                      <Row className="dark-artikel-data-row align-items-center">
                        {/* Col 1: Driver Info */}
                        <Col xs={12} md={4} className="dark-artikel-col">
                          <div className="dark-artikel-location-item">
                            <div className="address pickup">
                              <FaUser style={{ color: "#ff7b00" }} />
                              <span>
                                {renderHighlightedText(
                                  vehicle.driver_name,
                                  searchTerm,
                                ) || "N/A"}
                              </span>
                            </div>
                            <div className="datetime">
                              <FaPhone />
                              <span>{vehicle.phone_number || "N/A"}</span>
                            </div>
                          </div>
                        </Col>

                        {/* Arrow */}
                        <Col xs={12} md={1} className="dark-artikel-arrow-col">
                          {/* <span className="dark-artikel-simple-arrow text-white ">→</span> */}
                        </Col>

                        {/* Col 2: Vehicle Info */}
                        <Col xs={12} md={4} className="dark-artikel-col">
                          <div className="dark-artikel-location-item">
                            <div className="address delivery">
                              <FaCar style={{ color: "#ff7b00" }} />
                              <span>
                                {renderHighlightedText(
                                  vehicle.vehicle_type,
                                  searchTerm,
                                ) || "N/A"}
                              </span>
                            </div>
                            <div className="datetime">
                              <FaIdCard />
                              <span>{vehicle.vehicle_number || "N/A"}</span>
                            </div>
                          </div>
                        </Col>

                        {/* Col 3: Stats */}
                        <Col xs={6} md={2} className="dark-artikel-col">
                          <div className="dark-artikel-stat-row">
                            <FaWeightHanging
                              className="stat-icon"
                              style={{ color: "#888" }}
                            />
                            <span
                              className="stat-label"
                              style={{ color: "#888", marginLeft: "5px" }}
                            >
                              Max:
                            </span>
                            <span
                              className="stat-value"
                              style={{ color: "#ff7b00", marginLeft: "5px" }}
                            >
                              {vehicle.max_weight || "N/A"}
                            </span>
                          </div>
                          <div className="dark-artikel-stat-row">
                            <FaRuler
                              className="stat-icon"
                              style={{ color: "#888" }}
                            />
                            <span
                              className="stat-value stopps-value"
                              style={{ color: "#fff", marginLeft: "5px" }}
                            >
                              {vehicle.max_length || "N/A"} m
                            </span>
                          </div>
                        </Col>

                        {/* Col 4: Collapse Arrow */}
                        <Col xs={6} md={1} className="dark-artikel-col-right">
                          <div
                            className={`dark-artikel-collapse-icon ${activeKey === index.toString() ? "open" : ""}`}
                          >
                            <FaChevronUp
                              className="collapse-chevron"
                              style={{ color: "#ff7b00" }}
                            />
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Accordion.Header>

                  <Accordion.Body>
                    <div
                      // className="dark-bg-darker"
                      style={{ borderRadius: "12px", padding: "1rem" }}
                    >
                      <Row>
                        {/* Vehicle Details */}
                        <Col md={8}>
                          <div className="dark-section mb-3">
                            <div className="dark-section-title">
                              <FaTruck />
                              <span>
                                {t("vehiclesTable.vehicleDetails") ||
                                  "Vehicle Details"}
                              </span>
                            </div>
                            <div className="dark-section-body">
                              <Row>
                                <Col md={6}>
                                  <div className="dark-vehicle-info-block">
                                    <div className="dark-info-label">
                                      {t("vehiclesTable.driverName")}
                                    </div>
                                    <div className="dark-info-value">
                                      <FaUser />
                                      {vehicle.driver_name || "N/A"}
                                    </div>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="dark-vehicle-info-block">
                                    <div className="dark-info-label">
                                      {t("vehiclesTable.phoneNumber")}
                                    </div>
                                    <div className="dark-info-value">
                                      <FaPhone />
                                      {vehicle.phone_number || "N/A"}
                                    </div>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="dark-vehicle-info-block">
                                    <div className="dark-info-label">
                                      {t("vehiclesTable.vehicleType")}
                                    </div>
                                    <div className="dark-info-value">
                                      <FaCar />
                                      {vehicle.vehicle_type || "N/A"}
                                    </div>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="dark-vehicle-info-block">
                                    <div className="dark-info-label">
                                      {t("vehiclesTable.licenseNumber")}
                                    </div>
                                    <div className="dark-info-value">
                                      <FaIdCard />
                                      {vehicle.vehicle_number || "N/A"}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          </div>
                        </Col>

                        {/* Capacity & Actions */}
                        <Col md={4}>
                          <div className="dark-section">
                            <div className="dark-section-title">
                              <FaWeightHanging />
                              <span>
                                {t("vehiclesTable.capacity") || "Capacity"}
                              </span>
                            </div>
                            <div className="dark-section-body">
                              <div className="dark-vehicle-info-block">
                                <div className="dark-info-label">
                                  {t("vehiclesTable.maxWeight")}
                                </div>
                                <div className="dark-info-value">
                                  {vehicle.max_weight ||
                                    vehicle.weight ||
                                    "N/A"}
                                </div>
                              </div>
                              <div className="dark-vehicle-info-block">
                                <div className="dark-info-label">
                                  {t("vehiclesTable.maxLength")}
                                </div>
                                <div className="dark-info-value">
                                  {vehicle.max_length ||
                                    vehicle.length ||
                                    "N/A"}
                                </div>
                              </div>
                              <div className="dark-vehicle-info-block">
                                <div className="dark-info-label">
                                  {t("vehiclesTable.maxWidth")}
                                </div>
                                <div className="dark-info-value">
                                  {vehicle.max_width || vehicle.width || "N/A"}
                                </div>
                              </div>
                              <div className="dark-vehicle-info-block">
                                <div className="dark-info-label">
                                  {t("vehiclesTable.maxHeight")}
                                </div>
                                <div className="dark-info-value">
                                  {vehicle.max_height ||
                                    vehicle.height ||
                                    "N/A"}
                                </div>
                              </div>

                              {userType === "driver" && (
                                <Button
                                  variant="danger"
                                  className="dark-btn-cta mt-3"
                                  style={{
                                    backgroundColor: "#dc3545",
                                    borderColor: "#dc3545",
                                  }}
                                  onClick={() => handleDelete(vehicle.id)}
                                  disabled={formLoading || !vehicle.id}
                                >
                                  <FaTrashAlt /> {t("vehiclesTable.delete")}
                                </Button>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
          </Accordion>

          {/* Mobile View - Card Style matching DriverRequest design */}
          <div className="d-block d-md-none mb-5">
            {loading && (
              <div className="text-center py-4">
                <Spinner animation="border" variant="warning" />
                <p className="mt-2 dark-text-muted">{t("common.loading")}</p>
              </div>
            )}
            {!loading && displayData.length === 0 && (
              <p className="text-center p-3 dark-text-muted">
                {searchTerm
                  ? t("vehiclesTable.noMatchingVehicles")
                  : t("vehiclesTable.noVehiclesFound")}
              </p>
            )}
            {!loading &&
              displayData.map((vehicle, index) => (
                <div key={vehicle.id || index} className="dark-mobile-card">
                  {/* Header Row - Date/Time Info */}
                  <div
                    className="dark-mobile-card-header"
                    style={{ flexDirection: "column", gap: "0.5rem" }}
                  >
                    {/* Top Row: Calendar + Clock */}
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <div className="d-flex align-items-center gap-2">
                        <FaClock style={{ color: "#ff7b00" }} />
                        <span
                          className="dark-text-white"
                          style={{ fontSize: "0.9rem" }}
                        >
                          {vehicle.created_at
                            ? new Date(vehicle.created_at).toLocaleTimeString(
                              "de-DE",
                              { hour: "2-digit", minute: "2-digit" },
                            )
                            : "--:--"}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="dark-text-white"
                          style={{ fontSize: "0.9rem" }}
                        >
                          {vehicle.created_at
                            ? new Date(vehicle.created_at).toLocaleDateString(
                              "de-DE",
                            )
                            : "--.--.----"}
                        </span>
                        <FaCalendarAlt style={{ color: "#ff7b00" }} />
                      </div>
                    </div>

                    {/* Location Rows */}
                    <div className="d-flex justify-content-between align-items-start w-100 mt-2">
                      <div
                        className="d-flex flex-column gap-1"
                        style={{ flex: 1 }}
                      >
                        <span
                          className="dark-text-muted"
                          style={{ fontSize: "0.85rem" }}
                        >
                          km {vehicle.max_length || "N/A"}
                        </span>
                      </div>
                      <div className="d-flex flex-column align-items-end gap-1">
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="dark-text-white"
                            style={{ fontSize: "0.9rem" }}
                          >
                            {vehicle.driver_name || "N/A"}
                          </span>
                          <FaEye style={{ color: "#ff7b00" }} />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="dark-text-white"
                            style={{ fontSize: "0.9rem" }}
                          >
                            {vehicle.vehicle_type || "N/A"}
                          </span>
                          <FaEye style={{ color: "#888" }} />
                        </div>
                      </div>
                    </div>

                    {/* Weight/Count Row */}
                    <div className="d-flex justify-content-between align-items-center w-100 mt-1">
                      <div className="d-flex align-items-center gap-2">
                        <FaBox style={{ color: "#ff7b00" }} />
                        <span
                          className="dark-text-white"
                          style={{ fontSize: "0.85rem" }}
                        >
                          {formatNumber(vehicle.max_weight)} kg
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Body - Transport Type + Image */}
                  <div
                    className="dark-mobile-card-body"
                    style={{ textAlign: "center", paddingTop: "1rem" }}
                  >
                    {/* Transport Type Label */}
                    <div className="mb-3">
                      <span
                        className="dark-text-white"
                        style={{ fontSize: "1rem", fontWeight: "500" }}
                      >
                        {vehicle.vehicle_type || t("vehiclesTable.vehicle")}
                      </span>
                    </div>

                    {/* Vehicle Image */}
                    <div className="d-flex justify-content-center mb-3">
                      <img
                        src={getVehicleImage(vehicle.vehicle_type)}
                        alt={vehicle.vehicle_type}
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "contain",
                          borderRadius: "8px",
                          background: "rgba(255, 123, 0, 0.05)",
                          padding: "10px",
                        }}
                        onError={(e) => {
                          e.target.src = truckImg;
                        }}
                      />
                    </div>

                    {/* Details Button */}
                    <div
                      className="dark-mobile-footer"
                      style={{
                        justifyContent: "center",
                        borderTop: "none",
                        paddingTop: "0",
                      }}
                    >
                      <button
                        className="dark-mobile-details-btn"
                        onClick={() => toggleOffCanvas(index)}
                        style={{
                          width: "100%",
                          maxWidth: "300px",
                          padding: "12px 24px",
                          fontSize: "0.95rem",
                        }}
                      >
                        {t("driverRequest.buttons.details") || "Details"}
                        <FaChevronDown />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {/* Mobile OffCanvas for Details */}
          {!loading &&
            displayData.map((vehicle, index) => (
              <div key={`offcanvas-${index}`}>
                <div
                  className={`offcanvas-overlay ${offCanvasOpen === index ? "show" : ""}`}
                  onClick={() => setOffCanvasOpen(null)}
                />

                <div
                  className={`offcanvas-accordion-body ${offCanvasOpen === index ? "open" : ""}`}
                >
                  <button
                    className="offcanvas-close-btn"
                    onClick={() => setOffCanvasOpen(null)}
                  >
                    <FaTimes />
                  </button>

                  <Card
                    className="offcanvas-card dark-section"
                    style={{
                      background:
                        "linear-gradient(145deg, #2a2a2a 0%, #1e1e1e 50%, #252525 100%)",
                      border: "1px solid #3a3a3a",
                    }}
                  >
                    <Card.Body>
                      <div className="dark-section-title mb-3">
                        <FaTruck style={{ color: "#ff7b00" }} />
                        <span className="dark-text-white">
                          {t("vehiclesTable.vehicleDetails") ||
                            "Vehicle Details"}
                        </span>
                      </div>

                      <Row className="g-3">
                        <Col xs={12}>
                          <div className="dark-vehicle-info-block">
                            <div className="dark-info-label">
                              {t("vehiclesTable.driverName")}
                            </div>
                            <div className="dark-info-value">
                              <FaUser style={{ color: "#ff7b00" }} />
                              <span className="dark-text-white">
                                {vehicle.driver_name || "N/A"}
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xs={12}>
                          <div className="dark-vehicle-info-block">
                            <div className="dark-info-label">
                              {t("vehiclesTable.phoneNumber")}
                            </div>
                            <div className="dark-info-value">
                              <FaPhone style={{ color: "#ff7b00" }} />
                              <span className="dark-text-white">
                                {vehicle.phone_number || "N/A"}
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xs={12}>
                          <div className="dark-vehicle-info-block">
                            <div className="dark-info-label">
                              {t("vehiclesTable.vehicleType")}
                            </div>
                            <div className="dark-info-value">
                              <FaCar style={{ color: "#ff7b00" }} />
                              <span className="dark-text-white">
                                {vehicle.vehicle_type || "N/A"}
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xs={12}>
                          <div className="dark-vehicle-info-block">
                            <div className="dark-info-label">
                              {t("vehiclesTable.licenseNumber")}
                            </div>
                            <div className="dark-info-value">
                              <FaIdCard style={{ color: "#ff7b00" }} />
                              <span className="dark-text-white">
                                {vehicle.vehicle_number || "N/A"}
                              </span>
                            </div>
                          </div>
                        </Col>

                        <Col xs={12}>
                          <hr style={{ borderColor: "#3a3a3a" }} />
                        </Col>

                        <Col xs={6}>
                          <div className="dark-vehicle-info-block">
                            <div className="dark-info-label">
                              {t("vehiclesTable.maxWeight")}
                            </div>
                            <div className="dark-info-value">
                              <span className="dark-text-white">
                                {vehicle.max_weight || "N/A"}
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="dark-vehicle-info-block">
                            <div className="dark-info-label">
                              {t("vehiclesTable.maxLength")}
                            </div>
                            <div className="dark-info-value">
                              <span className="dark-text-white">
                                {vehicle.max_length || "N/A"}
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="dark-vehicle-info-block">
                            <div className="dark-info-label">
                              {t("vehiclesTable.maxWidth")}
                            </div>
                            <div className="dark-info-value">
                              <span className="dark-text-white">
                                {vehicle.max_width || "N/A"}
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="dark-vehicle-info-block">
                            <div className="dark-info-label">
                              {t("vehiclesTable.maxHeight")}
                            </div>
                            <div className="dark-info-value">
                              <span className="dark-text-white">
                                {vehicle.max_height || "N/A"}
                              </span>
                            </div>
                          </div>
                        </Col>

                        {userType === "driver" && (
                          <Col xs={12} className="mt-3">
                            <Button
                              variant="danger"
                              className="w-100"
                              style={{
                                borderRadius: "25px",
                                padding: "12px",
                                fontWeight: "600",
                              }}
                              onClick={() => {
                                setOffCanvasOpen(null);
                                handleDelete(vehicle.id);
                              }}
                              disabled={formLoading || !vehicle.id}
                            >
                              <FaTrashAlt className="me-2" />{" "}
                              {t("vehiclesTable.delete")}
                            </Button>
                          </Col>
                        )}
                      </Row>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            ))}

          {/* Pagination */}
          {!loading && pagination.last_page > 0 && !searchTerm && (
            <PaginationComponent
              currentPage={pagination.current_page}
              lastPage={pagination.last_page}
              onPageChange={handlePageChange}
              disabled={loading}
            />
          )}
        </div>

        
        {/* Form for adding new vehicle */}
        {userType === "driver" && (
          <div className="dark-section shadow-sm rounded-3 p-4 mt-4">
            <div className="dark-section-title mb-4">
              <FaTruck />
              <span style={{ fontSize: "1.2rem" }}>
                {t("vehiclesTable.vehicleTypeAndCapacity")}
              </span>
            </div>

            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.vehicleType")}
                  </Form.Label>
                  <Form.Select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!validationErrors.vehicle_type}
                    className="dark-bg-dark dark-text-white border-secondary"
                  >
                    {VEHICLE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                  {validationErrors.vehicle_type && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.vehicle_type[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.currentDriver")}
                  </Form.Label>
                  <Form.Control
                    name="driver"
                    value={formData.driver}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!validationErrors.driver_name}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.driver_name && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.driver_name[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.licenseNumber")}
                  </Form.Label>
                  <Form.Control
                    name="vehicle_number"
                    value={formData.vehicle_number}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!validationErrors.vehicle_number}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.vehicle_number && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.vehicle_number[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.phoneNumber")}
                  </Form.Label>
                  <Form.Control
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    type="tel"
                    isInvalid={!!validationErrors.phone_number}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.phone_number && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.phone_number[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.email") || "Email"}
                  </Form.Label>
                  <Form.Control
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    type="email"
                    isInvalid={!!validationErrors.email}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.email && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.email[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.password") || "Password"}
                  </Form.Label>
                  <Form.Control
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    type="password"
                    isInvalid={!!validationErrors.password}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.password && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.password[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.passwordConfirm") || "Confirm Password"}
                  </Form.Label>
                  <Form.Control
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleInputChange}
                    type="password"
                    isInvalid={!!validationErrors.password_confirmation}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.password_confirmation && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.password_confirmation[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.maxWeight")}
                  </Form.Label>
                  <Form.Control
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    type="number"
                    min="0"
                    isInvalid={!!validationErrors.max_weight}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.max_weight && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.max_weight[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.maxLength")}
                  </Form.Label>
                  <Form.Control
                    name="length"
                    value={formData.length}
                    onChange={handleInputChange}
                    type="number"
                    min="0"
                    isInvalid={!!validationErrors.max_length}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.max_length && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.max_length[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.maxWidth")}
                  </Form.Label>
                  <Form.Control
                    name="width"
                    value={formData.width}
                    onChange={handleInputChange}
                    type="number"
                    min="0"
                    isInvalid={!!validationErrors.max_width}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.max_width && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.max_width[0]}
                    </div>
                  )}
                </Col>

                <Col md={6}>
                  <Form.Label className="dark-text-white">
                    {t("vehiclesTable.maxHeight")}
                  </Form.Label>
                  <Form.Control
                    name="height"
                    value={formData.height}
                    onChange={handleInputChange}
                    type="number"
                    min="0"
                    isInvalid={!!validationErrors.max_height}
                    className="dark-bg-dark dark-text-white border-secondary"
                  />
                  {validationErrors.max_height && (
                    <div className="text-danger small mt-1 d-block">
                      {validationErrors.max_height[0]}
                    </div>
                  )}
                </Col>
              </Row>

              <div className="d-flex justify-content-center align-items-baseline gap-3 mt-5">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={resetForm}
                  disabled={formLoading}
                  style={{ borderRadius: "30px", padding: "8px 30px" }}
                  className="dark-text-white"
                >
                  {t("actions.reset")}
                </Button>

                <Button
                  type="submit"
                  className="bg-orange dark-btn-cta"
                  style={{
                    border: "none",
                    borderRadius: "30px",
                    padding: "8px 30px",
                    fontWeight: "bold",
                    width: "auto",
                  }}
                  disabled={formLoading}
                >
                  {formLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      {t("common.saving")}...
                    </>
                  ) : (
                    t("actions.save")
                  )}
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Container>

      {(userType === "client" || userType === "driver" || userType === "driver_company") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}
